/**
 * Deciding what a weight field should show while a scale is attached.
 *
 * A connected scale owns the field — the field *is* the readout, so it follows
 * the platter continuously rather than only at the moment of capture. Typing
 * takes it back. Getting the handover between those two right is the whole
 * problem, so it lives here where it can be tested.
 * @module mirroring
 */

export interface MirrorArgs {
  /** Live weight in pounds, or null when no scale is reporting. */
  pounds: number | null;
  /** What the field currently shows. */
  value: string;
  /** Whether the operator has typed over the scale since the field last cleared. */
  manual: boolean;
}

export interface MirrorResult {
  /** New field text, or null to leave it alone. */
  value: string | null;
  /** What `manual` should become. */
  manual: boolean;
}

/**
 * What the field should show for the latest reading.
 * @param args - See {@link MirrorArgs}.
 * @param args.pounds
 * @param args.value
 * @param args.manual
 * @returns The text to write, and whether the operator still owns the field.
 */
export function nextMirroredValue({
  pounds,
  value,
  manual,
}: MirrorArgs): MirrorResult {
  // Nothing reporting: leave whatever is there. A scale going away must not
  // wipe a weight the operator is part-way through using.
  if (pounds === null) return { value: null, manual };

  // An empty field hands control back. That's how a manual weight stops being
  // manual — the add cleared it, or the operator did — without needing a
  // separate signal from the caller.
  const owned = value.trim() === '' ? false : manual;
  if (owned) return { value: null, manual: true };

  const next = pounds.toFixed(2);
  // Only when it differs, so a settled scale doesn't re-render every field on
  // screen for a number that hasn't changed.
  return { value: next === value ? null : next, manual: false };
}
